/**
 * The admin app's URL patterns, defined once. `pattern` is for consumers that
 * match against the URL (route configs, `useMatch`, pattern-taking redirects;
 * a contract test keeps them matching) and everything that navigates or links
 * calls `build` — even parameterless paths, so a moved or reparameterized
 * path is a compile error at every link site rather than a broken URL.
 *
 * Project detail URLs are the one family defined elsewhere: use
 * `buildProjectUrlPath` from `@repo/app-types/helpers/adminAppRoutes` (it
 * also handles the optional interview segment and transcript fragment).
 */

import { generatePath, type PathParam } from "react-router";

type PathParams<Pattern extends string> = {
  readonly [Name in PathParam<Pattern>]: string;
};

// Zero arguments when the pattern has no params, so parameterless paths are
// still built (never linked via `pattern`) and gaining a param is a compile
// error at every link site instead of a silently broken URL.
type BuildArgs<Pattern extends string> = [PathParam<Pattern>] extends [never]
  ? []
  : [params: PathParams<Pattern>];

export interface AppPath<Pattern extends string> {
  /** The route pattern, with `:param` placeholders. */
  pattern: Pattern;
  /** Builds a concrete URL from the pattern's parameters. */
  build: (...args: BuildArgs<Pattern>) => string;
}

function definePath<const Pattern extends string>(
  pattern: Pattern,
): AppPath<Pattern> {
  return {
    pattern,
    build: (...args: readonly [PathParams<Pattern>?]) =>
      generatePath<string>(pattern, args[0]),
  };
}

const playbook = "/performance/playbooks/:playbookId";
const playbookAgent = `${playbook}/agents/:automationId`;
const playbookTeammate = `${playbook}/teammates/:teammateId`;
const teammateAgent = "/agents/:teammateAgentId";
// The teammate agent's Overview tab hosts a criterion drill-down that carries
// redundant teammate/playbook segments (see the note on its route).
const teammateAgentCriterion = `${teammateAgent}/overview/teammates/:teammateId/playbooks/:playbookId/criteria/:criterionId`;
const settings = "/settings";

export const appPaths = {
  home: definePath("/"),
  go: definePath("/go"),
  trainings: definePath("/train"),

  // Agents workspace
  teammateAgents: definePath("/agents"),
  teammateAgent: definePath(teammateAgent),
  teammateAgentOverview: definePath(`${teammateAgent}/overview`),
  teammateAgentConfiguration: definePath(`${teammateAgent}/configuration`),
  teammateAgentInstructions: definePath(`${teammateAgent}/instructions`),
  teammateAgentResourcesOutputs: definePath(
    `${teammateAgent}/resources-outputs`,
  ),
  teammateAgentTesting: definePath(`${teammateAgent}/testing`),
  teammateAgentCalls: definePath(`${teammateAgent}/customer-interactions`),
  teammateAgentCall: definePath(
    `${teammateAgent}/customer-interactions/:gradingId`,
  ),
  teammateAgentCallCriterion: definePath(
    `${teammateAgent}/customer-interactions/:gradingId/assessment/criteria/:criterionId`,
  ),
  teammateAgentCriterionCall: definePath(
    `${teammateAgentCriterion}/customer-interactions/:gradingId`,
  ),
  agentTools: definePath("/agent-tools"),
  agentTool: definePath("/agent-tools/:toolId"),
  agentResult: definePath("/agent-results/:resultId"),

  // Performance workspace
  playbooks: definePath("/performance/playbooks"),
  playbook: definePath(playbook),
  playbookDashboard: definePath(`${playbook}/dashboard`),
  playbookTeammates: definePath(`${playbook}/teammates`),
  playbookGroups: definePath(`${playbook}/groups`),
  playbookCalls: definePath(`${playbook}/customer-interactions`),
  playbookCall: definePath(`${playbook}/customer-interactions/:gradingId`),
  playbookCallAssessment: definePath(
    `${playbook}/customer-interactions/:gradingId/assessment`,
  ),
  playbookCallTranscript: definePath(
    `${playbook}/customer-interactions/:gradingId/transcript`,
  ),
  playbookCallCriterion: definePath(
    `${playbook}/customer-interactions/:gradingId/assessment/criteria/:criterionId`,
  ),
  playbookCriteria: definePath(`${playbook}/criteria`),
  playbookCriterion: definePath(`${playbook}/criteria/:criterionId`),
  playbookCriterionTeammate: definePath(
    `${playbook}/criteria/:criterionId/teammate/:teammateId`,
  ),
  playbookCriterionTeammateCall: definePath(
    `${playbook}/criteria/:criterionId/teammate/:teammateId/customer-interactions/:gradingId`,
  ),
  playbookAgents: definePath(`${playbook}/agents`),
  playbookAgent: definePath(playbookAgent),
  playbookAgentResults: definePath(`${playbookAgent}/results`),
  playbookAgentResult: definePath(`${playbookAgent}/results/:artifactId`),
  playbookAgentRuns: definePath(`${playbookAgent}/runs`),
  playbookAgentRun: definePath(`${playbookAgent}/runs/:runId`),
  playbookSignals: definePath(`${playbook}/signals`),
  playbookSignal: definePath(`${playbook}/signals/:signalId`),
  playbookTopics: definePath(`${playbook}/topics`),
  playbookTopic: definePath(`${playbook}/topics/:topicId`),
  playbookReports: definePath(`${playbook}/reports`),
  playbookReport: definePath(`${playbook}/reports/:reportId`),
  playbookReview: definePath(`${playbook}/review`),
  teammate: definePath(playbookTeammate),
  teammateCalls: definePath(`${playbookTeammate}/customer-interactions`),
  teammateCall: definePath(
    `${playbookTeammate}/customer-interactions/:gradingId`,
  ),
  teammateCallCriterion: definePath(
    `${playbookTeammate}/customer-interactions/:gradingId/assessment/criteria/:criterionId`,
  ),
  teammateCriterion: definePath(`${playbookTeammate}/criteria/:criterionId`),
  teammateCriterionCall: definePath(
    `${playbookTeammate}/criteria/:criterionId/customer-interactions/:gradingId`,
  ),
  teammateAchievements: definePath(`${playbookTeammate}/achievements`),
  teammateTraining: definePath(`${playbookTeammate}/trainings/:participantId`),
  teammateAgentResults: definePath(`${playbookTeammate}/agent-results`),
  teammateAgentResult: definePath(
    `${playbookTeammate}/agent-results/:artifactId`,
  ),

  // Recruiting/training workspaces
  project: definePath("/project/:projectId"),

  // Settings
  settings: definePath(settings),
  settingsProfile: definePath(`${settings}/profile`),
  settingsOrganization: definePath(`${settings}/organization`),
  settingsTeammates: definePath(`${settings}/teammates`),
  settingsTeammateGroups: definePath(`${settings}/teammates/groups`),
  settingsSignIn: definePath(`${settings}/teammates/sign-in`),
  settingsKeywordTrackers: definePath(`${settings}/keyword-trackers`),
  settingsForms: definePath(`${settings}/forms`),
  settingsMessageTemplates: definePath(`${settings}/message-templates`),
  settingsDataExtraction: definePath(`${settings}/data-extraction`),
  settingsCustomFields: definePath(`${settings}/custom-fields`),
  settingsRecording: definePath(`${settings}/call-recorder-recording`),
  settingsPlaybooks: definePath(`${settings}/playbooks`),
  // Served by the `:playbookId` editor route, which treats "new" as creation.
  settingsNewPlaybook: definePath(`${settings}/playbooks/new`),
  settingsPlaybook: definePath(`${settings}/playbooks/:playbookId`),
  settingsPlaybookEdit: definePath(`${settings}/playbooks/:playbookId/edit`),
  settingsPlaybookFeedback: definePath(
    `${settings}/playbooks/:playbookId/feedback`,
  ),
  settingsPlaybookSettings: definePath(
    `${settings}/playbooks/:playbookId/settings`,
  ),
  settingsKnowledgeBases: definePath(`${settings}/knowledge-bases`),
  settingsKnowledgeBase: definePath(
    `${settings}/knowledge-bases/:knowledgeBaseId`,
  ),
  settingsKnowledgeBaseAttachment: definePath(
    `${settings}/knowledge-bases/:knowledgeBaseId/attachments/:attachmentId`,
  ),
  settingsSignals: definePath(`${settings}/signals`),
  settingsNewSignal: definePath(`${settings}/signals/new`),
  settingsSignal: definePath(`${settings}/signals/:signalId`),
  settingsBilling: definePath(`${settings}/billing`),
  settingsIntegrations: definePath(`${settings}/integrations`),
  settingsIntegration: definePath(`${settings}/integrations/:integrationSlug`),
  settingsDevelopers: definePath(`${settings}/developers`),
  settingsWebhooks: definePath(`${settings}/webhooks`),
  settingsLiveCoaching: definePath(`${settings}/live-coaching`),
} as const;
